/*
 * TeamAvatar — circular team identity element.
 *
 * If the team has a logo (data URL or external URL), render it inside a
 * round mask. Otherwise fall back to the team's primary color as a solid
 * dot. Used wherever the app surfaces a team identity (scoreboard,
 * roster columns, match cards, leaderboard, etc.) so the brand is
 * consistent.
 *
 * Sized in pixels. The fallback dot can opt into a faint ring so it
 * reads against colored backgrounds (e.g. on top of a team-tinted
 * match-card row).
 */

interface TeamLike {
  logoUrl?: string | null;
  primaryColor?: string | null;
  name?: string;
}

interface Props {
  team: TeamLike;
  size?: number;
  /** Adds a 1px white/alpha ring around the avatar so it stays visible
   *  on top of colored backgrounds. Applies to both image and dot. */
  ring?: boolean;
}

export function TeamAvatar({ team, size = 14, ring = false }: Props) {
  const radius = size / 2;
  const ringStyle = ring ? { boxShadow: '0 0 0 1px rgba(255,255,255,0.2)' } : {};

  if (team.logoUrl) {
    return (
      <img
        src={team.logoUrl}
        alt={team.name ?? 'Team logo'}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          objectFit: 'cover',
          flexShrink: 0,
          background: '#0a0a0a',
          ...ringStyle,
        }}
      />
    );
  }

  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: radius,
        background: team.primaryColor ?? 'var(--text-mut)',
        flexShrink: 0,
        ...ringStyle,
      }}
    />
  );
}
